import { useEffect, useState } from "react";
import { useSearchParams } from 'react-router-dom';
import Row from "../row/row";
import {
  GroupRequirement,
  AdditionalInformation,
  RequirementColumn,
  RequirementFactory,
  Department,
  AnswerContext
} from '../../types/data';
import { arrow } from "../../ui-icons/icons";
import {
  calculatesAverageValue,
  filteredAnswers,
  calculateIndentColumn,
} from '../../utils/utils';
import { getProductionDepartments } from "../../utils/data-table";
import { Report } from '../../types/table';
import { TooltipItem } from "src/utils/Tooltip";

const COLUMNS = {
  '1': {
    width: '93px',
    indent: '0',
  },
  '2': {
    width: '93px',
    indent: '93px',
  },
  '3': {
    width: '93px',
    indent: '186px',
  },
  '4': {
    width: '170px',
    indent: '0',
    indentFactory: '279px',
  },
};

type RowBlockProps = GroupRequirement & {
  departments: Department[];
  showContent?: boolean;
  year: number;
  month: number;
  factory: number;
  category: Report;
  dataColumn?: RequirementFactory[];
  onOpenModal?: (data?: AdditionalInformation) => void;
  answerContext: AnswerContext;
  isCompleted?: boolean;
  onCreateQuestionnaire: () => void;
  context?: any;
  completionDate?: string;
  completedAssessment?: string;
};

function RowBlock(props: RowBlockProps) {
  const {title, groupRequirements, showContent, year, month, factory, category, dataColumn, departments, onOpenModal, onCreateQuestionnaire, isCompleted, answerContext, context, completionDate, completedAssessment } = props;

  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const getClassName = (value: number | 'N/A') => {
    if (value === 'N/A') return 'bg-gray-200';
    if (value <= 59) return 'bg-pink-300';
    if (value >= 60 && value <= 69) return 'bg-yellow-300';
    if (value >= 70) return 'bg-green-300';
  };

  const handleToggleChange = () => {
    if (category === 'factory') return;

    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (showContent) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [showContent]);

  useEffect(() => {
    const actionCardId = searchParams.get('actionCardVPO');

    if (actionCardId) {
      const allActions = [];
      groupRequirements.forEach(group =>
        group.requirements?.forEach(requirement =>
          requirement.answers?.forEach(answer => {
            if (answer.actions) allActions.push(...answer.actions);
          })
        )
      );
      console.log('row-block actionCardId:', actionCardId, 'allActions in block:', allActions, 'title:', title);

      const hasMatchingAction = groupRequirements.some(group =>
        group.requirements?.some(requirement =>
          requirement.answers?.some(answer =>
            answer.actions && answer.actions.includes(actionCardId)
          )
        )
      );

      if (hasMatchingAction) {
        // console.log('MATCH FOUND in block:', title);
        setIsOpen(true);
      }
    }
  }, [searchParams, groupRequirements]);

  return (
    <>
      <tr>
        {(category === 'factory' || category === 'region') && (
          <>
            <td
              className={`sticky z-10 border border-gray-400 bg-white px-2.5 py-2 text-xs before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
              style={{
                left: COLUMNS['1'].indent,
                width: `${COLUMNS['1'].width}`,
              }}
            >
              {year}
            </td>

            <td
              className={`sticky z-10 border border-gray-400 bg-white px-2.5 py-2 text-xs before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
              style={{
                left: COLUMNS['2'].indent,
                width: `${COLUMNS['2'].width}`,
              }}
            >
              {month}
            </td>

            {category === 'factory' && (
              <>
                <td
                  className={`sticky z-10 border border-gray-400 bg-white px-2.5 py-2 text-xs before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
                  style={{
                    left: COLUMNS['3'].indent,
                    width: `${COLUMNS['3'].width}`,
                  }}
                >
                  {factory}
                </td>
              </>
            )}
          </>
        )}

        <td
          colSpan={category === 'factory' ? 1 : 4}
          onClick={handleToggleChange}
          className={`sticky z-10 border border-gray-400 bg-white px-2.5 py-2 text-xs after:absolute after:bottom-[-1px] after:left-0 after:block after:w-full after:h-[1px] after:bg-gray-400 before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400 ${category === 'factory' && 'group'}`}
          style={{
            left: calculateIndentColumn(category, COLUMNS['4']),
            width: `${COLUMNS['4'].width}`,
          }}
        >
          <div
            className={`text-sm font-medium ${category === 'factory' && 'overflow-hidden text-ellipsis whitespace-nowrap pl-2'} ${isOpen ? 'opacity-100' : 'opacity-50'}`}
          >
            <span>{title}</span>
          </div>

          {category === 'factory' &&
            <div className="absolute left-[-2px] bottom-1 bg-white rounded-lg p-2 shadow-custom whitespace-nowrap hidden group-hover:block text-sm text-[#637381]">
            {title}
          </div>}


          {category !== 'factory' && (
            <div
              className={`absolute right-3 top-[50%] translate-y-[-50%] ${isOpen && 'rotate-180'}`}
            >
              <TooltipItem className={`${isOpen ? 'rotate-180 translate-x-[-140%]' : ''}`} position="right" tooltipsText={isOpen ? 'Свернуть' : 'Развернуть'}>
                {arrow}
              </TooltipItem>
            </div>
          )}
        </td>

        {category !== 'region' &&
          departments && departments.length > 0 && departments.map((el: any) => (
            <td
              key={el.id}
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(groupRequirements, el.title))}`}
            >
              {calculatesAverageValue(groupRequirements, el.title)}
              {calculatesAverageValue(groupRequirements, el.title) !== 'N/A' &&
                '%'}
            </td>
          ))}

        {category !== 'region' && (
          <>
            <td
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(
                filteredAnswers(groupRequirements, getProductionDepartments(departments)),
                undefined,
                true,
              ))}`}
            >
              {calculatesAverageValue(
                filteredAnswers(groupRequirements, getProductionDepartments(departments)),
                undefined,
                true,
              )}
              {calculatesAverageValue(
                filteredAnswers(groupRequirements, getProductionDepartments(departments)),
                undefined,
                true,
              ) !== 'N/A' && '%'}
            </td>

            <td
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(groupRequirements, undefined, true))}`}
            >
              {calculatesAverageValue(groupRequirements, undefined, true)}
              {calculatesAverageValue(groupRequirements, undefined, true) !==
                'N/A' && '%'}
            </td>
          </>
        )}

        {category === 'region' &&
          dataColumn &&
          dataColumn.map((el, index) => (
            <td
              key={index}
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(groupRequirements, undefined, undefined, el.shortTitle))}`}
            >
              {calculatesAverageValue(
                groupRequirements,
                undefined,
                undefined,
                el.shortTitle,
              )}
              {calculatesAverageValue(
                groupRequirements,
                undefined,
                undefined,
                el.shortTitle,
              ) !== 'N/A' && '%'}
            </td>
          ))}

        {category === 'region' && dataColumn && dataColumn.length > 1 && (
          <th
            className={`w-[116px] border border-gray-400 px-2.5 py-4 text-center text-xs font-medium ${getClassName(
              calculatesAverageValue(
                groupRequirements,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
              ),
            )}`}
          >
            {calculatesAverageValue(
              groupRequirements,
              undefined,
              undefined,
              undefined,
              undefined,
              true,
            )}
            {calculatesAverageValue(
              groupRequirements,
              undefined,
              undefined,
              undefined,
              undefined,
              true,
            ) !== 'N/A' && '%'}
          </th>
        )}

        {category === 'factory' && (
          <>
            <td className="border border-gray-400 px-2.5 py-2 text-center text-xs">
              {completedAssessment}
            </td>

            <td className="border border-gray-400 px-2.5 py-2 text-center text-xs">
              {completionDate}
            </td>
          </>
        )}
      </tr>

      {isOpen &&
        groupRequirements.map((requirement, index) => (
            <Row
              key={index}
              year={year}
              month={month}
              factory={factory}
              departments={departments}
              category={category}
              answerContext={answerContext}
              onOpenModal={onOpenModal}
              context={context}
              onCreateQuestionnaire={onCreateQuestionnaire}
              {...requirement}
              isCompleted={isCompleted as boolean}
            />
         ))}
    </>
  );
}

export default RowBlock;
